import { consumeSSE } from "../lib/sseParser";
import { resumeExecution } from "../api/execution";
import { ExecutionEvent } from "../execution/executionEvents";

export type StreamExecutionOptions = {
    baseUrl: string;
    executionId: string;
    initialResponse: Response; // The response from POST /stream
    onEvent: (sequence: number, event: ExecutionEvent) => void;
    onDisconnect: () => void;
    onConnected: () => void;
    signal: AbortSignal;
    api_key?: string;
    additional_headers?: { [key: string]: string } | string;
};

const RETRY_DELAYS = [1000, 2000, 4000, 8000, 16000, 30000];

export async function streamExecutionWithReconnect(options: StreamExecutionOptions) {
    let { 
        baseUrl, 
        executionId, 
        initialResponse, 
        onEvent, 
        onDisconnect, 
        onConnected, 
        signal, 
        api_key, 
        additional_headers 
    } = options;
    
    let lastSequence = 0;
    let retryAttempt = 0;
    let currentResponse: Response | null = initialResponse;
    let isTerminal = false;

    // Helper wrapper for the onEvent callback
    const handleEvent = (id: string, eventType: string, data: any) => {
        const seq = parseInt(id, 10) || 0;
        if (seq > lastSequence) {
            lastSequence = seq;
        }

        const typedEvent = { type: eventType, data } as ExecutionEvent;
        
        if (typedEvent.type === "execution.queued" && data.execution_id) {
            executionId = data.execution_id;
        }

        if (
            typedEvent.type === "execution.completed" || 
            typedEvent.type === "execution.failed" || 
            typedEvent.type === "execution.cancelled"
        ) {
            isTerminal = true;
        }

        onEvent(seq, typedEvent);
    };

    while (!signal.aborted && !isTerminal) {
        try {
            if (currentResponse) {
                if (!currentResponse.ok) {
                    throw new Error(`HTTP Error: ${currentResponse.status}`);
                }
                
                onConnected();
                
                await consumeSSE(currentResponse, handleEvent, () => {
                    // Aborted by user
                });
            }
        } catch (error: any) {
            if (signal.aborted) break;
            
            console.error("SSE Connection error:", error);
            onDisconnect();

            const delay = RETRY_DELAYS[Math.min(retryAttempt, RETRY_DELAYS.length - 1)];
            await new Promise(res => setTimeout(res, delay));
            retryAttempt++;
        }

        if (signal.aborted || isTerminal) {
            break;
        }

        // Reconnect via GET /executions/{executionId}/stream
        if (executionId) {
            try {
                currentResponse = await resumeExecution(
                    baseUrl,
                    executionId,
                    lastSequence,
                    api_key,
                    additional_headers
                );
                if (currentResponse.ok) {
                    retryAttempt = 0;
                }
            } catch (err) {
                currentResponse = null;
            }
        } else {
            // Cannot resume without an execution ID, break the loop
            break;
        }
    }
}
